import { lazy, Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import SplashScreen from './components/SplashScreen'
import Navbar from './components/layout/Navbar'
import Footer from './components/layout/Footer'
import { PromoBannerSlot, PromoFloatingSlot, PromoPopupSlot } from './components/promo/PromoSlots'
import OnboardingGate from './components/OnboardingGate'
import EnrollCoursePage from './pages/EnrollCourse'
import CourseRoomPage from './pages/CourseRoom'
import AccountPage from './pages/Account'
import CertificateView from './pages/Certificates/CertificateView'
import ReceiptView from './pages/Receipt/ReceiptView'
import InhouseRequestPage from './pages/InhouseRequest'
import BooksPage from './pages/Books'
import BookDetailPage from './pages/BookDetail'
import CartPage from './pages/Cart'
import CartCheckoutPage from './pages/CartCheckout'
import PaymentResultPage from './pages/PaymentResult'
import InhouseInvoiceView from './pages/InhouseRequest/InvoiceView'
import PostJobPage from './pages/PostJob'
import JobApplicationPage from './pages/JobApplication'
import LoginPage from './pages/Login'
import RegisterPage from './pages/Register'
import VerifyEmailPage from './pages/VerifyEmail'
import OnboardingPage from './pages/Onboarding'
import PreEmploymentAssessment from './pages/Assessment/PreEmploymentAssessment'
import ProtectedRoute from './components/ProtectedRoute'

const HomePage = lazy(() => import('./pages/Home'))
const CoursesPage = lazy(() => import('./pages/Courses/CoursesPage'))
const CourseDetailPage = lazy(() => import('./pages/CourseDetail/CourseDetailPage'))
const JobsPage = lazy(() => import('./pages/Jobs'))
const JobDetailPage = lazy(() => import('./pages/Jobs/JobDetailPage'))
const EmployersPage = lazy(() => import('./pages/Employers'))
const InstructorDetailPage = lazy(() => import('./pages/InstructorDetail'))
const InstructorsPage = lazy(() => import('./pages/Instructors'))
const RefundPage = lazy(() => import('./pages/Refund'))
const PrivacyPage = lazy(() => import('./pages/Legal/PrivacyPage'))
const PrivacyPolicyPage = lazy(() => import('./pages/Legal/PrivacyPolicyPage'))
const TermsPage = lazy(() => import('./pages/Legal/TermsPage'))
const HelpPage = lazy(() => import('./pages/Legal/HelpPage'))
const NotFoundBlock = lazy(() => import('./components/ui/NotFoundBlock'))

// /jobs/:id no longer has its own detail page — the split-view /jobs list already
// shows full job detail inline, so this path forwards straight into the apply flow.
function JobDetailRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/jobs/${id}/apply`} replace />
}

// เลื่อนขึ้นบนสุดทุกครั้งที่เปลี่ยนหน้า (path เปลี่ยน) — React Router ไม่ทำให้อัตโนมัติ ไม่งั้นสกรอลล์
// ค้างตำแหน่งเดิมจากหน้าก่อน (เช่นเลื่อนลงอ่านหน้า courses ค้างไว้ แล้วกดลิงก์ไปหน้าอื่นจะโผล่กลางหน้าเปล่าๆ)
function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/10 border-t-accent" />
    </div>
  )
}


export default function App() {
  const [splashDone, setSplashDone] = useState(false)

  return (
    <>
      <AnimatePresence>
        {!splashDone && <SplashScreen onDone={() => setSplashDone(true)} />}
      </AnimatePresence>

      {splashDone && (
        <BrowserRouter>
          <ScrollToTop />
          <OnboardingGate>
          <Routes>
            {/* ── Full-screen (ไม่มี Navbar/Footer) ── */}
            <Route path="/course-room" element={<CourseRoomPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
            {/* แบบประเมินก่อนสมัครงาน — เป็นแบบประเมินกลาง ไม่ผูกกับตำแหน่งงานใดตำแหน่งหนึ่ง ทำครั้งเดียวใช้ได้กับทุกใบสมัคร
                ทำ Navbar เองในตัว (ไม่ใช้ layout กลาง) */}
            <Route path="/pre-assessment" element={<ProtectedRoute><PreEmploymentAssessment /></ProtectedRoute>} />

            {/* ── Pages with Navbar/Footer ── */}
            <Route
              path="*"
              element={
                <div className="flex min-h-screen flex-col">
                  <Navbar />
                  <PromoBannerSlot />
                  <div className="flex-1">
                    <Suspense fallback={<PageLoader />}>
                      <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/courses" element={<CoursesPage />} />
                        <Route path="/trainings" element={<CoursesPage format="ONSITE" />} />
                        <Route path="/courses/:slug" element={<CourseDetailPage />} />
                        <Route path="/books" element={<BooksPage />} />
                        <Route path="/books/:slug" element={<BookDetailPage />} />
                        <Route path="/book-checkout" element={<Navigate to="/cart" replace />} />
                        <Route path="/jobs" element={<JobsPage />} />
                        <Route path="/jobs/:id" element={<JobDetailRedirect />} />
                        <Route path="/jobs/:id/detail" element={<JobDetailPage />} />
                        <Route path="/employers" element={<EmployersPage />} />
                        <Route path="/refund" element={<RefundPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/help" element={<HelpPage />} />
                        <Route path="/instructors" element={<InstructorsPage />} />
                        <Route path="/instructors/:id" element={<InstructorDetailPage />} />
                        <Route path="/enroll-course" element={<ProtectedRoute><EnrollCoursePage /></ProtectedRoute>} />
                        <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
                        <Route path="/checkout/:kind" element={<ProtectedRoute><CartCheckoutPage /></ProtectedRoute>} />
                        <Route path="/payment/result/:orderId" element={<ProtectedRoute><PaymentResultPage /></ProtectedRoute>} />
                        <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
                        <Route path="/profile/edit" element={<Navigate to="/account?tab=profile" replace />} />
                        <Route path="/certificates/:enrollmentId" element={<ProtectedRoute><CertificateView /></ProtectedRoute>} />
                        <Route path="/receipt/:orderId" element={<ProtectedRoute><ReceiptView /></ProtectedRoute>} />
                        <Route path="/inhouse-requests/:id" element={<ProtectedRoute><InhouseRequestPage /></ProtectedRoute>} />
                        <Route path="/inhouse-requests/:id/invoice" element={<ProtectedRoute><InhouseInvoiceView /></ProtectedRoute>} />
                        {/* เส้นทางเดิม — เปลี่ยนมาเป็น tab เดียวกันใน /account แล้ว แต่กัน link เก่าที่ยังชี้มาที่นี่พัง */}
                        <Route path="/profile" element={<Navigate to="/account?tab=profile" replace />} />
                        <Route path="/my-courses" element={<Navigate to="/account?tab=courses" replace />} />
                        <Route path="/certificates" element={<Navigate to="/account?tab=certificates" replace />} />
                        <Route path="/wishlist" element={<Navigate to="/account?tab=wishlist" replace />} />
                        <Route path="/post-job" element={<ProtectedRoute requireCompany><PostJobPage /></ProtectedRoute>} />
                        <Route path="/jobs/:id/apply" element={<ProtectedRoute><JobApplicationPage /></ProtectedRoute>} />
                        {/* path ที่ไม่มีอยู่จริง → หน้า 404 (เดิมตกไปหน้าแรก ทำให้พิมพ์ URL มั่วก็เปิดได้) */}
                        <Route path="*" element={<NotFoundBlock />} />
                      </Routes>
                    </Suspense>
                  </div>
                  <Footer />
                  <PromoFloatingSlot />
                  <PromoPopupSlot />
                </div>
              }
            />
          </Routes>
          </OnboardingGate>
        </BrowserRouter>
      )}
    </>
  )
}